import { useEffect, useMemo, useState } from "react";
import "@/styles/demo-new-applicant-flow.css";
import { useFlow, type ConnectedSource } from "@/contexts/FlowContext";

/**
 * /demo-new/applicant-flow — React port of the bespoke prototype previously
 * served from public/demo-new/applicant-flow.html.
 *
 * Visual parity is preserved by importing the prototype's CSS verbatim,
 * scoped under `.demo-new-root` so it cannot leak. State machine mirrors the
 * original (5 steps + submitted), but OAuth connections now flow through
 * FlowContext.connectSource / disconnectSource — same pattern as the
 * /demo/applicant-flow synthetic-session demo.
 *
 * No backend calls. Real applicantApi.startOAuth remains scoped to /verify/:token.
 */

type ProviderId = "google" | "microsoft" | "linkedin" | "facebook" | "instagram" | "amazon";
type EmailMethod = "" | "google" | "microsoft" | "application";

const steps = ["intro", "email", "supporting", "vouchers", "review"] as const;
const stepNames = ["Consent", "Email", "Accounts", "Vouches", "Review"];

const socialProviders: { id: ProviderId; title: string; detail: string; icon: string }[] = [
  { id: "linkedin", title: "LinkedIn", detail: "Name & email", icon: "in" },
  { id: "facebook", title: "Facebook", detail: "Name & email", icon: "f" },
  { id: "instagram", title: "Instagram", detail: "Name & email", icon: "ig" },
  { id: "amazon", title: "Amazon", detail: "Account history", icon: "a" },
];

const otherProviders: { id: ProviderId; title: string; detail: string; icon: string }[] = [
  { id: "google", title: "Google", detail: "Name & email", icon: "G" },
  { id: "microsoft", title: "Microsoft", detail: "Hotmail, Live, or Work", icon: "M" },
];

const fallbackVouches = [
  { name: "Sarah Johnson", email: "sarah@example.com" },
  { name: "Marcus Lee", email: "marcus@example.com" },
  { name: "Priya Patel", email: "priya@example.com" },
];

const pretty = (id: string): string =>
  ({ google: "Google", microsoft: "Microsoft", linkedin: "LinkedIn", facebook: "Facebook", instagram: "Instagram", amazon: "Amazon" } as Record<string, string>)[id] || id;

const sourceFromProvider = (id: ProviderId): ConnectedSource => {
  const social: ProviderId[] = ["linkedin", "facebook", "instagram"];
  const isSocial = social.includes(id);
  return {
    id,
    name: pretty(id),
    category: isSocial ? "social" : "other",
    tenure: "—",
    corroboration: isSocial ? "Social account aligned" : "Account identity verified",
    strength: id === "facebook" || id === "instagram" ? "limited" : "moderate",
  };
};

interface Voucher {
  name: string;
  email: string;
  relationship?: string;
  status: "empty" | "draft" | "invite pending";
}

export default function DemoNewApplicantFlow() {
  const { state: flowState, connectSource, disconnectSource } = useFlow();

  // OAuth set is *derived* from FlowContext so the demo state lives in one place,
  // matching the /demo/applicant-flow pattern.
  const oauth = useMemo<Set<ProviderId>>(() => {
    const ids: ProviderId[] = ["google", "microsoft", "linkedin", "facebook", "instagram", "amazon"];
    return new Set(ids.filter((id) => flowState.connectedSources.some((s) => s.id === id)));
  }, [flowState.connectedSources]);

  const [step, setStep] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);
  const [emailMethod, setEmailMethod] = useState<EmailMethod>("");
  const [selectedEmailMethod, setSelectedEmailMethod] = useState<EmailMethod>("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifiedEmails, setVerifiedEmails] = useState<Set<Exclude<EmailMethod, "">>>(new Set());
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [voucherRows, setVoucherRows] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [accountNoticeSeen, setAccountNoticeSeen] = useState(() => {
    try {
      return sessionStorage.getItem("digavouch.accountNoticeSeen.v3") === "true";
    } catch { return false; }
  });
  const [pendingAccountCheck, setPendingAccountCheck] = useState<ProviderId | "">("");
  const [modalOpen, setModalOpen] = useState(false);
  // Simulated OAuth interstitial — { provider, context: "email" | "account" }
  const [oauthFlow, setOauthFlow] = useState<{ provider: ProviderId; context: "email" | "account" } | null>(null);
  // OTP modal for the "Quick code" email path
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  // "View details" modal on the review screen
  const [detailsOpen, setDetailsOpen] = useState(false);

  const applicant = "Patrick";

  // Visible account-check count includes Google/Microsoft if used for email.
  const visibleAccountChecks = useMemo<ProviderId[]>(() => {
    const set = new Set<ProviderId>(oauth);
    verifiedEmails.forEach((m) => { if (m === "google" || m === "microsoft") set.add(m); });
    return [...set];
  }, [oauth, verifiedEmails]);

  const requiredVouchCount = (): number => {
    const c = visibleAccountChecks.length;
    if (c >= 3) return 1;
    if (c === 2) return 2;
    return 3;
  };

  const signalCount = (extra = 0) => Math.min(4, Math.max(1, 1 + visibleAccountChecks.length + extra));

  const toggleOauth = (id: ProviderId) => {
    if (oauth.has(id)) {
      disconnectSource(id);
    } else {
      connectSource(sourceFromProvider(id));
    }
  };

  // Simulated OAuth login: shows an interstitial for ~1.2s, then completes.
  const startOAuth = (id: ProviderId, context: "email" | "account") => {
    setOauthFlow({ provider: id, context });
    window.setTimeout(() => {
      if (context === "email" && (id === "google" || id === "microsoft")) {
        setEmailMethod((prev) => prev || id);
        setSelectedEmailMethod(id);
        setEmailVerified(true);
        setVerifiedEmails((prev) => {
          if (prev.has(id)) return prev;
          const next = new Set(prev); next.add(id); return next;
        });
        if (!oauth.has(id)) connectSource(sourceFromProvider(id));
      } else if (context === "account") {
        if (!oauth.has(id)) connectSource(sourceFromProvider(id));
      }
      setOauthFlow(null);
    }, 1200);
  };

  const requestAccountCheck = (id: ProviderId) => {
    if (oauth.has(id)) {
      // Allow disconnect by re-clicking a connected provider.
      disconnectSource(id);
      return;
    }
    if (accountNoticeSeen) {
      startOAuth(id, "account");
      return;
    }
    setPendingAccountCheck(id);
    setModalOpen(true);
  };

  const markAccountNoticeSeen = () => {
    setAccountNoticeSeen(true);
    try { sessionStorage.setItem("digavouch.accountNoticeSeen.v3", "true"); } catch { /* storage unavailable — non-fatal */ }
  };

  const closeModal = () => { setModalOpen(false); setPendingAccountCheck(""); };

  const goToStep = (n: number) => {
    setStep(Math.max(0, Math.min(steps.length, n)));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "auto" });
  };

  const onAcceptBegin = () => {
    if (!consentChecked) return;
    setEmailVerified(false);
    setEmailMethod("");
    setSelectedEmailMethod("");
    setVerifiedEmails(new Set());
    goToStep(1);
  };

  // Clicking an email-method row now triggers the action directly.
  const onEmailMethodClick = (id: EmailMethod) => {
    if (!id) return;
    setSelectedEmailMethod(id);
    if (id === "google" || id === "microsoft") {
      startOAuth(id, "email");
    } else if (id === "application") {
      setOtpOpen(true);
    }
  };

  const onOtpSubmit = () => {
    if (otpCode.trim().length < 4) return;
    setEmailMethod((prev) => prev || "application");
    setEmailVerified(true);
    setVerifiedEmails((prev) => {
      if (prev.has("application")) return prev;
      const next = new Set(prev); next.add("application"); return next;
    });
    setOtpOpen(false);
    setOtpCode("");
  };

  const onEmailContinue = () => {
    if (emailVerified) { goToStep(2); return; }
    if (!selectedEmailMethod) return;
    onEmailMethodClick(selectedEmailMethod);
  };

  const ensureRequiredVouchesSent = () => {
    const required = requiredVouchCount();
    // Payload guardrail: only the active required slots (rows the user can
    // currently see) are marked as invite-pending and would be transmitted.
    // Hidden retention data is left untouched in state but excluded from the
    // outbound payload via Array.slice(0, required).
    const visible = Math.max(required, voucherRows);
    const sendCount = required; // strict — never auto-dispatch beyond required
    const next: Voucher[] = [...vouchers];
    for (let i = 0; i < sendCount; i++) {
      const fb = fallbackVouches[i] || { name: `Vouch ${i + 1}`, email: "pending@example.com" };
      const ex = next[i] || {} as Voucher;
      next[i] = {
        name: ex.name || fb.name,
        email: ex.email || fb.email,
        relationship: ex.relationship || "Employer",
        status: "invite pending",
      };
    }
    // Trim any trailing hidden rows beyond the visible slot count so review
    // never resurfaces invites the user can't see.
    setVouchers(next.slice(0, visible).filter((v) => v && (v.name || v.email)));
    setVoucherRows(Math.max(voucherRows, sendCount));
  };

  const updateVoucher = (idx: number, field: keyof Voucher, value: string) => {
    setVouchers((prev) => {
      const next = [...prev];
      const existing = next[idx] || { name: "", email: "", relationship: "", status: "empty" as const };
      const merged = { ...existing, [field]: value };
      merged.status = merged.name || merged.email ? "draft" : "empty";
      next[idx] = merged;
      return next;
    });
  };

  // --- Derived for review ---
  const invited = vouchers.filter((v) => v.status === "invite pending");
  const required = requiredVouchCount();
  const missingVouches = Math.max(0, required - invited.length);
  const accounts = visibleAccountChecks;
  const emailLabel = emailMethod === "google" || emailMethod === "microsoft" ? pretty(emailMethod) : "patrick@email.com";
  const emailDetail = emailMethod === "google" || emailMethod === "microsoft" ? "patrick@email.com · Verified from your application" : "Verified from your application";

  const headline = step === 0 ? `${applicant}, you're already off to a great start` : `${applicant}, help us confirm a few more details`;
  const heroCopy = step === 0
    ? "Rockwell Properties already has part of what they need. This step helps you securely share a few more identity details so they can keep your application moving."
    : "We already have part of what we need. To keep your application moving, connect a few accounts or invite someone who can vouch for you.";
  const stepLabel = step < steps.length ? `Step ${step + 1} of ${steps.length}` : "";
  const mobileStepTitle = step < steps.length ? `Step ${step + 1} of ${steps.length} · ${stepNames[step]}` : "";

  // Background uses prototype's --bg token.
  useEffect(() => { document.body.classList.add("demo-new-body"); return () => document.body.classList.remove("demo-new-body"); }, []);

  const pageClass = `page demo-new-root${step === 0 ? " intro-mode" : ""}${step === 1 ? " email-mode" : ""}${step >= steps.length ? " submitted-mode" : ""}${step >= 1 ? " app-mode" : ""}`;

  const relationshipIcon = (rel: string) => {
    if (/landlord|property manager/i.test(rel)) {
      return (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><path d="M3 10l9-7 9 7v11H3z" /><path d="M9 21v-7h6v7" /></svg>
      );
    }
    if (/employer|supervisor|professional|coworker/i.test(rel)) {
      return (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-6h6v6" /></svg>
      );
    }
    if (/professor|academic|advisor/i.test(rel)) {
      return (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><path d="M22 10 12 4 2 10l10 6 10-6z" /><path d="M6 12v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" /></svg>
      );
    }
    return (
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><circle cx={12} cy={8} r={4} /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" /></svg>
    );
  };

  const Stepper = () => {
    if (step === 0 || step >= steps.length) return <div className="stepper" aria-label="Verification steps" />;
    return (
      <div className="stepper" aria-label="Verification steps">
        {steps.map((_, i) => (
          <div key={i} style={{ display: "contents" }}>
            <div className={`step-wrap${i === step ? " active" : ""}`}>
              <button
                type="button"
                className={`step-node${i < step ? " done" : ""}${i === step ? " active" : ""}`}
                aria-label={`Go to ${stepNames[i]}`}
                onClick={() => goToStep(i)}
              >
                {i < step ? "✓" : String(i + 1)}
              </button>
              <span className="step-wrap-label">{stepNames[i]}</span>
            </div>
            {i < steps.length - 1 && <div className={`step-line${i < step ? " done" : ""}`} />}
          </div>
        ))}
      </div>
    );
  };

  const SignalMeter = ({ filled }: { filled: number }) => {
    const count = visibleAccountChecks.length;
    const accountText = count >= 3 ? "Account checks complete" : `${count} account check${count === 1 ? "" : "s"} added`;
    return (
      <div className="signal-meter">
        <span>Email verified · {accountText}</span>
        <div className="signal-bars" aria-hidden="true">
          {[1, 2, 3, 4].map((i) => <span key={i} className={i <= filled ? "active" : ""} />)}
        </div>
      </div>
    );
  };

  const ProviderRow = (p: { id: ProviderId; title: string; detail: string; icon: string }) => {
    const usedForEmail = (p.id === "google" || p.id === "microsoft") && verifiedEmails.has(p.id);
    const connected = oauth.has(p.id) || usedForEmail;
    return (
      <button
        key={p.id}
        className={`provider-row${connected ? " connected" : ""}`}
        data-oauth={p.id}
        type="button"
        disabled={usedForEmail}
        onClick={() => !usedForEmail && requestAccountCheck(p.id)}
      >
        <span className="provider-icon">{p.icon}</span>
        <span>
          <strong>{p.title}</strong>
          <small>{usedForEmail ? "Email verified + account connected" : p.detail}</small>
        </span>
        <em>{connected ? "Checked" : "Connect"}</em>
      </button>
    );
  };

  // Visible slot count is anchored to `required`. Any extra rows the user
  // explicitly added via "+ Add another person" live in `voucherRows`, which
  // gets clamped down whenever `required` drops. Previously typed data for
  // hidden slots is intentionally retained in `vouchers` state (so toggling
  // accounts back and forth doesn't wipe input) — it just isn't rendered or
  // submitted while the requirement is lower.
  const visibleVoucherRows = step === 3 ? Math.max(required, voucherRows) : 0;

  useEffect(() => {
    // Clamp explicit "add another" extras down to the current requirement
    // whenever the requirement decreases. Hidden voucher data is preserved.
    setVoucherRows((r) => Math.min(r, required));
  }, [required]);
  const vouchCopy = (() => {
    const c = visibleAccountChecks.length;
    const profiles = c === 1 ? "1 digital profile" : `${c} digital profiles`;
    const refs = required === 1 ? "1 person" : `${required} people`;
    if (c >= 3) return { title: "Invite one person who knows you", body: "They'll only be asked to confirm they know you and verify their own identity." };
    if (c === 2) return { title: "Invite 2 people who know you", body: `Verifying ${profiles} reduces your required manual references to ${refs}.` };
    return { title: "Invite 3 people who know you", body: `Verifying ${profiles} reduces your required manual references to ${refs}.` };
  })();

  const submitDisabled = missingVouches > 0 || submitted;

  return (
    <main className={pageClass}>

      {step > 0 && step < steps.length && (
        <header className="hero hero-compact">
          <div className="mobile-step-title">{mobileStepTitle}</div>
          <Stepper />
          <div className="step-label">{stepLabel}</div>
        </header>
      )}

      {/* Intro */}
      <section className={`card${step === 0 ? "" : " hidden"}`} data-screen="intro">
        <div className="intro-trust">
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
          <span>Encrypted & private · Connections disconnected after review</span>
        </div>
        <div className="intro-hero">
          <div className="intro-property">
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
            Rockwell Properties
          </div>
          <h2>Hi {applicant}, let's confirm your identity</h2>
          <p>A few quick steps to help keep your application moving.</p>
        </div>
        <div className="intro-body">
          <div className="intro-time-wrap">
            <div className="intro-time">
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><circle cx={12} cy={12} r={10} /><path d="M12 6v6l4 2" /></svg>
              Takes about 3 minutes
            </div>
          </div>
          <div className="intro-steps">
            <div className="intro-step">
              <div className="intro-step-icon"><svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x={2} y={4} width={20} height={16} rx={2} /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg></div>
              <div><strong>Verify your email</strong><span>Google, Microsoft, or quick code</span></div>
            </div>
            <div className="intro-step">
              <div className="intro-step-icon"><svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx={12} cy={8} r={4} /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg></div>
              <div><strong>Connect an account</strong><span>Choose an approved account to help confirm it's you (the more, the stronger)</span></div>
            </div>
            <div className="intro-step">
              <div className="intro-step-icon"><svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx={9} cy={7} r={4} /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg></div>
              <div><strong>Ask someone who knows you</strong><span>They'll get a short message to confirm they know you</span></div>
            </div>
          </div>
          <div className="intro-consent">
            <label>
              <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
              <span>
                I agree to share the identity details I choose to provide with <strong>Rockwell Properties</strong> for this application.
                <span className="consent-note">DigaVouch does not make the rental decision.</span>
                <span className="consent-links"><a href="#">User Policy</a> · <a href="#">Terms of Service</a></span>
              </span>
            </label>
          </div>
          <div className="intro-actions">
            <button className="primary intro-begin" disabled={!consentChecked} onClick={onAcceptBegin}><span>→</span> Start Identity Review</button>
            <button className="intro-decline" type="button">Decline & return to application</button>
          </div>
        </div>
      </section>

      {/* Email */}
      <section className={`card${step === 1 ? "" : " hidden"}`} data-screen="email">
        <div className="app-card-head">
          <h2>Verify your email</h2>
          <p>Confirm the email on your application is yours.</p>
        </div>
        <div className="app-card-body">

          <div className="flow-privacy">
            <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            <span><strong>Private by design.</strong><br />We only confirm the account is yours. Your emails and content stay private.</span>
          </div>
          <p className="flow-section-label">Your application email</p>
          <div className={`verified-email-card${emailVerified ? "" : " pending"}`}>
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#437a22" strokeWidth={2}><rect x={2} y={4} width={20} height={16} rx={2} /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
            <div>
              <strong>patrick@email.com</strong>
              <span>{emailVerified ? "✓ Verified" : "Email on your application"}</span>
            </div>
            <span className="verified-check">✉</span>
          </div>
          <p className="flow-section-label">Other ways to verify</p>
          <div className="connect-list">
            {(["google", "microsoft", "application"] as const).map((id) => {
              const connected = id ? verifiedEmails.has(id) : false;
              const selected = selectedEmailMethod === id;
              const actionLabel = connected ? "Email verified" : selected ? "Selected" : id === "application" ? "Use code" : "Connect";
              const icon = id === "google" ? "G" : id === "microsoft" ? "M" : "✉";
              const title = id === "google" ? "Google" : id === "microsoft" ? "Microsoft" : "Quick code";
              const sub = id === "google" ? "Sign in with Google" : id === "microsoft" ? "Hotmail, Live, or Work" : "Send a one-time email code";
              return (
                <button
                  key={id}
                  type="button"
                  className={`connect-row${connected ? " connected" : ""}${selected && !connected ? " selected" : ""}`}
                  onClick={() => onEmailMethodClick(id)}
                >
                  <span className={`connect-logo ${id === "google" ? "google" : id === "microsoft" ? "microsoft" : ""}`}>{icon}</span>
                  <span>
                    <strong>{title}</strong>
                    <small>{sub}</small>
                  </span>
                  <em>{actionLabel}</em>
                </button>
              );
            })}
          </div>
          <div className="intro-actions">
            <button
              className="primary flow-continue"
              type="button"
              disabled={!selectedEmailMethod && !emailVerified}
              onClick={onEmailContinue}
            >
              {emailVerified ? "→ Verify & Continue" : selectedEmailMethod === "google" ? "→ Continue with Google" : selectedEmailMethod === "microsoft" ? "→ Continue with Microsoft" : selectedEmailMethod === "application" ? "→ Send Code" : "Choose a verification method"}
            </button>
          </div>
        </div>
      </section>

      {/* Supporting accounts */}
      <section className={`card${step === 2 ? "" : " hidden"}`} data-screen="supporting">
        <div className="app-card-head">
          <h2>Connect an account</h2>
          <p>Add one or more approved accounts — the more you connect, the stronger your verification.</p>
        </div>
        <div className="app-card-body">
          <SignalMeter filled={signalCount()} />
          <div className="flow-privacy">
            <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            <span><strong>Private by design.</strong><br />No account access shared. Messages and content stay private.</span>
          </div>
          <button className="learn-link" type="button" onClick={() => { setPendingAccountCheck(""); setModalOpen(true); }}>How account checks work</button>
          <div className="provider-list">
            <div className="provider-section">
              <p className="flow-section-label">Social accounts</p>
              {socialProviders.map((p) => <ProviderRow key={p.id} {...p} />)}
            </div>
            <div className="provider-section">
              <p className="flow-section-label">Other account options</p>
              {otherProviders.map((p) => <ProviderRow key={p.id} {...p} />)}
            </div>
          </div>
          <div className="intro-actions">
            <button className="primary flow-continue" type="button" onClick={() => goToStep(3)}><span>→</span> Continue</button>
            <button className="flow-skip" type="button" onClick={() => goToStep(3)}>I'll invite more people instead</button>
          </div>
        </div>
      </section>

      {/* Vouchers */}
      <section className={`card${step === 3 ? "" : " hidden"}`} data-screen="vouchers">
        <div className="app-card-head">
          <h2>Invite someone who knows you</h2>
        </div>
        <div className="app-card-body">

          <div className="vouch-status">
            <div className="vouch-status-copy">
              <strong>{vouchCopy.title}</strong>
              <span>{vouchCopy.body}</span>
              {required !== 1 && (
                <>
                  <span style={{ marginTop: 8 }}>Want to invite fewer people? Add another account check instead.</span>
                  <button type="button" className="link-button" onClick={() => goToStep(2)} style={{ marginTop: 6, alignSelf: "flex-start" }}>Add another account check instead</button>
                </>
              )}
            </div>
            <div className="vouch-status-strength" aria-label={`Verification tracker: ${visibleAccountChecks.length} of 4 tracks verified`}>
              <span className="vouch-strength-label">Verification tracker</span>
              <div className="signal-bars" aria-hidden="true">
                {[1, 2, 3, 4].map((i) => (
                  <span key={i} className={i <= visibleAccountChecks.length ? "active" : ""}>
                    {i <= visibleAccountChecks.length ? "✓" : ""}
                  </span>
                ))}
              </div>
              <span className="vouch-strength-count">{visibleAccountChecks.length} of 4 tracks verified</span>
            </div>
          </div>

          <div className="flow-privacy">
            <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            <span>{required === 1 ? "The person you invite will only confirm they know you — they won't see your application details." : "The people you invite only confirm they know you — they won't see your application details."}</span>
          </div>
          <p className="flow-section-label">Who can vouch for you?</p>
          <div>
            {Array.from({ length: visibleVoucherRows }, (_, i) => {
              const v = vouchers[i] || { name: "", email: "", relationship: "", status: "empty" as const };
              const sent = v.status === "invite pending";
              const ready = !sent && (v.name || v.email || v.relationship);
              const relIcon = relationshipIcon(v.relationship || "");
              return (
                <div key={i} className="vouch-grid">
                  <input value={v.name || ""} placeholder="Full name" autoComplete="name" aria-label={`Vouch ${i + 1} full name`} onChange={(e) => updateVoucher(i, "name", e.target.value)} />
                  <input value={v.email || ""} placeholder="Email" autoComplete="email" aria-label={`Vouch ${i + 1} email address`} onChange={(e) => updateVoucher(i, "email", e.target.value)} />
                  <div className={`select-with-icon${v.relationship ? " has-value" : ""}`}>
                    <span className="select-icon" aria-hidden="true">{relIcon}</span>
                    <select value={v.relationship || ""} aria-label={`Vouch ${i + 1} relationship`} onChange={(e) => updateVoucher(i, "relationship", e.target.value)}>
                      <option value="">Relationship</option>
                      {["Previous Landlord / Property Manager", "Current Employer / Supervisor", "Professional Reference", "Coworker", "Professor / Academic Advisor", "Roommate", "Character Reference (Non-Family)", "Other"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="relationship-helper">Choose how this person knows you.</div>
                  {sent && <div className="fine-print"><span className="badge green">Invite sent</span></div>}
                  {ready && <div className="fine-print"><span className="badge neutral">Ready to invite</span></div>}
                </div>
              );
            })}
          </div>
          <button className="add-person" type="button" onClick={() => setVoucherRows(Math.max(required, voucherRows) + 1)}>＋ Add another person</button>
          <div className="intro-actions">
            <button className="primary flow-continue" type="button" onClick={() => { ensureRequiredVouchesSent(); goToStep(4); }}><span>→</span> Send & Continue</button>
          </div>
        </div>
      </section>


      {/* Review */}
      <section className={`card${step === 4 ? "" : " hidden"}`} data-screen="review">
        <div className="app-card-head">
          <h2>Review & submit</h2>
          <p>Check the summary below, then submit your identity review to Rockwell Properties.</p>
        </div>
        <div className="app-card-body">
          <div>
            <div className="review-header">
              <p className="flow-section-label">Summary</p>
              <button className="flow-skip" type="button" onClick={() => setDetailsOpen(true)}>View details</button>
            </div>
            <div className="review-list">
              <div className="review-row complete">
                <span className="review-icon">✓</span>
                <span><strong>Email verified</strong><small>{emailLabel} · {emailDetail}</small></span>
              </div>
              <div className={`review-row${accounts.length ? " complete" : ""}`}>
                <span className="review-icon">{accounts.length ? "✓" : "•"}</span>
                <span><strong>{accounts.length ? "Account complete" : "Account"}</strong><small>{accounts.length ? `${accounts.length} account${accounts.length === 1 ? "" : "s"} checked` : "Not added"}</small></span>
              </div>
              <div className={`review-row${invited.length ? " complete" : ""}`}>
                <span className="review-icon">{invited.length ? "✓" : "•"}</span>
                <span>
                  <strong>{invited.length ? `${invited.length} reference${invited.length === 1 ? "" : "s"} staged to send` : "References to vouch for you"}</strong>
                  <small>{invited.length ? "Invitations queued — they'll go out when you submit." : `Invite ${required} ${required === 1 ? "person" : "people"} to continue.`}</small>
                </span>
              </div>
            </div>
            {missingVouches > 0 && (
              <div className="vouch-status">
                <strong>Invite {missingVouches} more {missingVouches === 1 ? "person" : "people"} to continue.</strong>
                <span>Go back and send {missingVouches === 1 ? "one more invite" : `${missingVouches} more invites`} before submitting.</span>
              </div>
            )}
          </div>
          <div className="legal-note">Once you submit, <strong>Rockwell Properties</strong> will be notified. DigaVouch shares the details you chose to provide, not account access.</div>
          <div className="intro-actions">
            <button className="primary flow-continue" type="button" disabled={submitDisabled} onClick={() => { if (invited.length >= required) { setSubmitted(true); goToStep(5); } }}>
              {submitted ? "Submitted" : "✓ Submit Identity Review"}
            </button>
            <button className="flow-skip" type="button" onClick={() => goToStep(2)}>Go back and edit</button>
          </div>
        </div>
      </section>

      {/* Submitted */}
      <section className={`card${step === 5 ? "" : " hidden"}`} data-screen="submitted">
        <div className="app-card-head">
          <h2>Identity review submitted</h2>
          <p>Your identity review has been sent to Rockwell Properties.</p>
        </div>
        <div className="app-card-body">
          <div className="legal-note"><strong>Your connections have been disconnected.</strong> DigaVouch shared only the details you chose to provide. DigaVouch does not make the rental decision.</div>
          <p className="flow-section-label">What happens next</p>
          <div className="next-steps">
            <div className="next-step-row">
              <span className="review-icon">✓</span>
              <span><strong>Rockwell Properties reviews your identity details</strong><small>The property manager will follow up if they need anything else.</small></span>
            </div>
            <div className="next-step-row">
              <span className="review-icon">✓</span>
              <span><strong>Your invited contacts receive their requests</strong><small>They'll only be asked to confirm they know you.</small></span>
            </div>
          </div>
          <div className="intro-actions">
            <button className="secondary" type="button" onClick={() => {
              const lines: string[] = [];
              lines.push("DigaVouch — Identity Review Summary");
              lines.push(`Submitted: ${new Date().toLocaleString()}`);
              lines.push(`Applicant: ${applicant}`);
              lines.push(`Property: Rockwell Properties`);
              lines.push("");
              lines.push(`Email verified: ${emailLabel} (${emailDetail})`);
              lines.push("");
              lines.push(`Connected accounts (${accounts.length}):`);
              accounts.forEach((a) => lines.push(`  • ${pretty(a)}`));
              lines.push("");
              lines.push(`Vouchers invited (${invited.length}):`);
              invited.forEach((v) => lines.push(`  • ${v.name || "(unnamed)"} <${v.email || ""}> — ${v.relationship || ""}`));
              const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `digavouch-identity-review-${new Date().toISOString().slice(0,10)}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}>Download a copy</button>
            <button className="secondary" type="button">Email me a copy</button>
            <button className="primary flow-continue" type="button">Return to Application</button>
          </div>
        </div>
      </section>

      {modalOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="accountModalTitle" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="account-modal">
            <div className="account-modal-head">
              <span className="review-icon">◇</span>
              <strong id="accountModalTitle">Account checks are private</strong>
              <button className="account-modal-close" type="button" aria-label="Close" onClick={closeModal}>×</button>
            </div>
            <p>DigaVouch only runs a one-time identity check on the account you choose. Rockwell Properties does not get access to your account.</p>
            <ul>
              <li>We confirm the account belongs to you</li>
              <li>We check basic details needed for identity review</li>
              <li>We do not read messages, posts, photos, or personal content</li>
              <li>We do not store passwords or credentials</li>
              <li>Connections are disconnected after review</li>
            </ul>
            <div className="account-modal-actions">
              <button className="secondary" type="button" onClick={closeModal}>Cancel</button>
              <button
                className="primary"
                type="button"
                onClick={() => {
                  const id = pendingAccountCheck;
                  markAccountNoticeSeen();
                  closeModal();
                  if (id) startOAuth(id, "account");
                }}
              >
                {pendingAccountCheck ? "Continue securely" : "Got it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simulated OAuth interstitial */}
      {oauthFlow && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="account-modal" style={{ textAlign: "center" }}>
            <div className="account-modal-head" style={{ justifyContent: "center" }}>
              <strong>Continuing to {pretty(oauthFlow.provider)}…</strong>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "8px 0 4px" }}>
              <div
                aria-hidden="true"
                style={{
                  width: 40, height: 40, borderRadius: "50%",
                  border: "3px solid #e6e3f5", borderTopColor: "#6d28d9",
                  animation: "demoNewSpin 0.9s linear infinite",
                }}
              />
              <p style={{ margin: 0, fontSize: 14, color: "#687386" }}>
                Securely confirming your {oauthFlow.provider === "google" || oauthFlow.provider === "microsoft" ? "email and " : ""}account with {pretty(oauthFlow.provider)}.
              </p>
            </div>
          </div>
          <style>{`@keyframes demoNewSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Quick-code OTP modal */}
      {otpOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) { setOtpOpen(false); setOtpCode(""); } }}>
          <div className="account-modal">
            <div className="account-modal-head">
              <span className="review-icon">✉</span>
              <strong>Enter the code we sent</strong>
              <button className="account-modal-close" type="button" aria-label="Close" onClick={() => { setOtpOpen(false); setOtpCode(""); }}>×</button>
            </div>
            <p>We sent a 6-digit code to <strong>patrick@email.com</strong>. Enter it below to verify your application email.</p>
            <input
              autoFocus
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="123456"
              aria-label="One-time code"
              style={{ width: "100%", padding: "12px 14px", fontSize: 18, letterSpacing: 4, textAlign: "center", border: "1px solid #cbd7e2", borderRadius: 10, marginTop: 8 }}
            />
            <div className="account-modal-actions">
              <button className="secondary" type="button" onClick={() => { setOtpOpen(false); setOtpCode(""); }}>Cancel</button>
              <button className="primary" type="button" disabled={otpCode.length < 4} onClick={onOtpSubmit}>Verify email</button>
            </div>
          </div>
        </div>
      )}

      {/* Review → View details modal */}
      {detailsOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setDetailsOpen(false); }}>
          <div className="account-modal">
            <div className="account-modal-head">
              <span className="review-icon">i</span>
              <strong>Verification details</strong>
              <button className="account-modal-close" type="button" aria-label="Close" onClick={() => setDetailsOpen(false)}>×</button>
            </div>
            <p style={{ marginBottom: 4 }}><strong>Email</strong></p>
            <ul>
              <li>{emailLabel} — {emailVerified ? "Verified" : "Not verified"}</li>
            </ul>
            <p style={{ marginBottom: 4 }}><strong>Connected accounts ({accounts.length})</strong></p>
            {accounts.length ? (
              <ul>
                {accounts.map((id) => <li key={id}>{pretty(id)} — Account verified</li>)}
              </ul>
            ) : (
              <p style={{ color: "#687386", marginTop: 0 }}>No accounts connected.</p>
            )}
            <p style={{ marginBottom: 4 }}><strong>People invited to vouch ({invited.length})</strong></p>
            {invited.length ? (
              <ul>
                {invited.map((v, i) => <li key={i}>{v.name} ({v.email}) — {v.relationship || "Relationship not set"} · Invitation queued</li>)}
              </ul>
            ) : (
              <p style={{ color: "#687386", marginTop: 0 }}>No one invited yet.</p>
            )}
            <div className="account-modal-actions">
              <button className="primary" type="button" onClick={() => setDetailsOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
